/**
 * FeatureSpotlightDialog (#1876, part 2 of #1735) - the one-time product
 * updates announcement shown after sign-in.
 *
 * Mounted ONCE in AppContent next to TipToast. Reads the same GET /api/tips
 * response the toast uses; the `updates` list arrives pre-localized and
 * newest first. An update is unseen when its publishedAt is later than the
 * user's watermark `preferences.updatesSeenAt`; a user with no watermark
 * falls back to their account createdAt, so a new user never gets the
 * historical backlog on first sign-in. With neither the dialog stays shut.
 *
 * Any close (Got it, See all updates, backdrop, Escape) records the
 * watermark through PUT /api/profile with the FULL merged preferences
 * (the backend replaces the object wholesale) and reports open/closed to
 * the parent through onOpenChange so TipToast can stay out of the way.
 *
 * Never opens while a guide is running (the intro guide auto-starts for new
 * users); it re-evaluates when the guide ends.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { IconSparkles } from '@tabler/icons-react';
import { apiGlobal } from '../services/api';
import { updateProfile } from '../services/profileAPI';
import { useUserSession } from '../contexts/UserSessionContext.jsx';
import { useGuides } from '../user_guides/GuidesProvider.jsx';

export const UPDATES_PATH = '/updates';

/** Local calendar date in the user's guide language. */
export const formatPublished = (iso, locale) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return d.toLocaleDateString(locale || undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }
};

/** The watermark an update must be newer than; null means never show. */
export const seenWatermark = (user) => {
  const seen = user?.preferences?.updatesSeenAt;
  if (typeof seen === 'string' && seen) return seen;
  const created = user?.createdAt;
  return typeof created === 'string' && created ? created : null;
};

export const unseenUpdates = (updates, watermark) => {
  if (!watermark) return [];
  return (updates || []).filter((u) => typeof u.publishedAt === 'string' && u.publishedAt > watermark);
};

const FeatureSpotlightDialog = ({ onOpenChange }) => {
  const session = useUserSession();
  const guides = useGuides();
  const navigate = useNavigate();
  const [updates, setUpdates] = useState(null);
  const [open, setOpen] = useState(false);
  const [watermark, setWatermark] = useState(null);
  const [profileVersion, setProfileVersion] = useState(null);
  const decidedRef = useRef(false);

  useEffect(() => {
    if (!session.user) return;
    setWatermark(seenWatermark(session.user));
    if (typeof session.user.version === 'number') setProfileVersion(session.user.version);
  }, [session.user]);

  useEffect(() => {
    if (session.loading || !session.user) return;
    let alive = true;
    apiGlobal.get('/tips')
      .then((res) => { if (alive) setUpdates(res?.updates || []); })
      .catch((err) => { console.warn('FeatureSpotlightDialog: load failed', err); });
    return () => { alive = false; };
  }, [session.loading, session.user]);

  const unseen = unseenUpdates(updates, watermark);

  // Decide once per session: open when something is unseen and no guide is
  // on screen. A running guide defers the decision, it does not cancel it.
  useEffect(() => {
    if (decidedRef.current || updates === null || guides.isRunning) return;
    decidedRef.current = true;
    if (unseen.length > 0) setOpen(true);
  }, [updates, unseen.length, guides.isRunning]);

  useEffect(() => {
    if (onOpenChange) onOpenChange(open);
  }, [open, onOpenChange]);

  const acknowledge = useCallback(() => {
    const now = new Date().toISOString();
    setWatermark(now);
    setOpen(false);
    if (typeof profileVersion === 'number') {
      const preferences = { ...(session.user?.preferences || {}), updatesSeenAt: now };
      updateProfile({ preferences, version: profileVersion })
        .then((res) => { if (res?.user?.version != null) setProfileVersion(res.user.version); })
        .catch(() => {});
    }
  }, [profileVersion, session.user]);

  const go = (path) => {
    acknowledge();
    navigate(path);
  };

  if (!open) return null;

  return (
    <Dialog
      open
      onClose={acknowledge}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      aria-labelledby="feature-spotlight-title"
      data-testid="feature-spotlight-dialog"
    >
      <DialogTitle id="feature-spotlight-title" sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Box
          sx={(t) => ({
            flexShrink: 0,
            width: 30,
            height: 30,
            borderRadius: `${t.ds.radius.icon}px`,
            backgroundColor: t.palette.tint.interactive,
            color: t.palette.interactive,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          })}
        >
          <IconSparkles size={18} />
        </Box>
        What's new
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing="14px">
          {unseen.map((u) => (
            <Box key={u.updateId} data-testid={`spotlight-update-${u.updateId}`}>
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'text.primary' }}>
                {u.title}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                {formatPublished(u.publishedAt, guides.language)}
              </Typography>
              <Typography sx={{ fontSize: '13px', color: 'text.secondary', mt: '4px', whiteSpace: 'pre-line' }}>
                {u.body}
                {u.linkPath && (
                  <>
                    {' '}
                    <MuiLink
                      component="button"
                      type="button"
                      onClick={() => go(u.linkPath)}
                      sx={{ fontSize: '13px', color: 'interactive', verticalAlign: 'baseline' }}
                    >
                      Take me there
                    </MuiLink>
                  </>
                )}
              </Typography>
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => go(UPDATES_PATH)}>See all updates</Button>
        <Button variant="contained" onClick={acknowledge}>Got it</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeatureSpotlightDialog;
